import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest, HttpMethod } from '@api/common';
import { queryKeys, showApiError } from '@api/queryClient';
import toastService from '@services/toastService';
import { EntryType } from '~types/types';
import { FlowMeter } from './flowMeters';

// ============================================================================
// Types
// ============================================================================

export interface ExternalData {
  key: string;
  value: string;
}

export interface TruckCarrier {
  licencePlate: string;
  forwardingCompany: string;
  sourceAddress: string;
  destinationAddress: string;
  receiver: string;
  billOfLandingNumber: string;
  reference: string;
}

export interface ShipCarrier {
  shipName: string;
}

export interface TrainCarrier {
  reference: string;
  carNumber: string;
  billOfLandingNumber: string;
}

export interface ContainerTrainCarrier {
  reference: string;
  containerNumber: string;
  billOfLandingNumber: string;
}

export interface ContainerTruckCarrier {
  licencePlate: string;
  containerNumber: string;
  billOfLandingNumber: string;
}

export interface PipeCarrier {
  destinationAddress: string;
}

export interface ProductMixtureEntry {
  productId: string;
  productName: string;
  productCode: string;
  tankId: string;
  tankName: string;
  portion: number;
}

export enum ActionDirection {
  Incoming = 0,
  Outgoing = 1,
}

export enum ShipmentType {
  Truck = 0,
  Ship = 1,
  Pipe = 2,
  Train = 3,
  ContainerTrain = 4,
  ContainerTruck = 5,
}

export enum entryStatus {
  incomplete = 0,
  verified = 1,
  approved = 2,
}

export enum entrySource {
  User = 0,
  System = 1,
  TruckScale = 2,
  TrainAuthority = 3,
  Automation = 4,
}

export interface BookkeepingEntry {
  id: string;
  created?: string;
  createdBy: string;
  modified?: string;
  modifiedBy: string;
  approved?: string;
  approvedBy: string;
  actionDirection: ActionDirection;
  terminalId?: string;
  terminalName?: string;
  tankId?: string;
  tankName?: string;
  customerId?: string;
  customerName?: string;
  productId?: string;
  productAliasId?: string;
  productName?: string;
  productCode?: string;
  productMixtureId: string;
  shipmentType: ShipmentType;
  entryType: EntryType;
  shipmentTypeName: string;
  unloadingStarted: string;
  unloadingFinished: string;
  unloadingStartedTimeSelected: boolean;
  unloadingFinishedTimeSelected: boolean;
  amount?: number;
  truck: TruckCarrier;
  ship: ShipCarrier;
  train: TrainCarrier;
  pipe: PipeCarrier;
  containerTrain: ContainerTrainCarrier;
  containerTruck: ContainerTruckCarrier;
  notes?: string;
  entryState: entryStatus;
  source: entrySource;
  externalData?: ExternalData[];
  billingInformation?: string;
  order: string;
  externalDataId?: string;
  measurementId?: string;
  flowMeterData?: ExternalData[];
  flowMeterComponentId?: string;
  hydrogenPeroxideFlowMeterData?: ExternalData[];
  hydrogenPeroxideFlowMeterId?: string;
  concentrationPercent: number;
  mixtures?: ProductMixtureEntry[];
  sendToCustomer?: boolean;
  lastSentToCustomer?: string;
}

export interface UnattachedFlowMeter extends FlowMeter {
  flowMeterData: ExternalData[];
}

export interface UnattachedFlowMeterHydrogenPeroxide {
  id: string;
  dateTime: string;
  tankName: string;
  tankId: string;
  amount: number;
  register: string;
  flowMeterData?: ExternalData[];
  concentrationPercent?: number;
}

export interface FetchEntriesParams {
  terminalId?: string;
  tankId?: string;
  from?: string;
}

export interface ApprovedEntryNotes {
  id: string;
  notes?: string;
  billingInformation?: string;
  sendToCustomer: boolean;
}

// ============================================================================
// API Functions
// ============================================================================

const API_ENDPOINT = 'bookkeepings';
const PRODUCT_MIXTURE_ENDPOINT = 'productmixturesbookkeeping';
const FLOW_METERS_ENDPOINT = 'flowmeters';

const buildQueryString = (params: FetchEntriesParams): string => {
  const parts: string[] = [];
  if (params.terminalId) parts.push(`terminalId=${params.terminalId}`);
  if (params.tankId) parts.push(`tankId=${params.tankId}`);
  if (params.from) parts.push(`from=${params.from}`);
  return parts.length > 0 ? `?${parts.join('&')}` : '';
};

const fetchLooseEntries = async (params: FetchEntriesParams): Promise<BookkeepingEntry[]> => {
  const queryString = buildQueryString(params);
  return apiRequest<void, BookkeepingEntry[]>(HttpMethod.GET, `${API_ENDPOINT}/loose${queryString}`);
};

const fetchApprovedEntries = async (params: FetchEntriesParams): Promise<BookkeepingEntry[]> => {
  const queryString = buildQueryString(params);
  return apiRequest<void, BookkeepingEntry[]>(HttpMethod.GET, `${API_ENDPOINT}/approved${queryString}`);
};

const fetchEntryForEdit = async (id: string): Promise<BookkeepingEntry> => {
  return apiRequest<void, BookkeepingEntry>(HttpMethod.GET, `${API_ENDPOINT}/edit/${id}`);
};

const createEntry = async (entry: BookkeepingEntry): Promise<BookkeepingEntry> => {
  return apiRequest<BookkeepingEntry, BookkeepingEntry>(HttpMethod.POST, API_ENDPOINT, entry);
};

const createProductMixtureEntry = async (entry: BookkeepingEntry): Promise<BookkeepingEntry[]> => {
  return apiRequest<BookkeepingEntry, BookkeepingEntry[]>(HttpMethod.POST, PRODUCT_MIXTURE_ENDPOINT, entry);
};

const updateEntry = async (entry: BookkeepingEntry): Promise<BookkeepingEntry> => {
  const endpoint = entry.entryType === EntryType.Mixture ? PRODUCT_MIXTURE_ENDPOINT : API_ENDPOINT;
  return apiRequest<BookkeepingEntry, BookkeepingEntry>(HttpMethod.PUT, endpoint, entry);
};

const updateApprovedEntryNotes = async (payload: ApprovedEntryNotes): Promise<BookkeepingEntry> => {
  return apiRequest<ApprovedEntryNotes, BookkeepingEntry>(HttpMethod.PUT, `${API_ENDPOINT}/approved`, payload);
};

const deleteEntry = async (params: { id: string; isProductMixture: boolean }): Promise<string[]> => {
  const endpoint = params.isProductMixture ? PRODUCT_MIXTURE_ENDPOINT : API_ENDPOINT;
  return apiRequest<void, string[]>(HttpMethod.DELETE, `${endpoint}/${params.id}`);
};

const fetchUnattachedFlowMeters = async (
  dateTime: string,
  bookkeepingEntryId: string
): Promise<UnattachedFlowMeter[]> => {
  return apiRequest<void, UnattachedFlowMeter[]>(
    HttpMethod.GET,
    `${FLOW_METERS_ENDPOINT}/unattached/${dateTime}/?bookkeeppingId=${bookkeepingEntryId}`
  );
};

const fetchUnattachedFlowMetersHydrogenPeroxide = async (
  dateTime: string,
  bookkeepingEntryId: string
): Promise<UnattachedFlowMeterHydrogenPeroxide[]> => {
  return apiRequest<void, UnattachedFlowMeterHydrogenPeroxide[]>(
    HttpMethod.GET,
    `${FLOW_METERS_ENDPOINT}/hydrogenperoxide/unattached/${dateTime}/?bookkeeppingId=${bookkeepingEntryId}`
  );
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch loose (unapproved) bookkeeping entries
 */
export const useLooseEntriesQuery = (params: FetchEntriesParams, enabled: boolean = true) => {
  return useQuery({
    queryKey: queryKeys.inventory.list({ ...params, type: 'loose' }),
    queryFn: () => fetchLooseEntries(params),
    enabled,
  });
};

/**
 * Fetch approved bookkeeping entries
 */
export const useApprovedEntriesQuery = (params: FetchEntriesParams, enabled: boolean = true) => {
  return useQuery({
    queryKey: queryKeys.inventory.list({ ...params, type: 'approved' }),
    queryFn: () => fetchApprovedEntries(params),
    enabled,
  });
};

/**
 * Fetch both loose and approved entries
 * Returns combined entries sorted by order
 */
export const useEntriesQuery = (params: FetchEntriesParams, enabled: boolean = true) => {
  const looseQuery = useLooseEntriesQuery(params, enabled);
  const approvedQuery = useApprovedEntriesQuery(params, enabled);

  const entries = [
    ...(looseQuery.data || []),
    ...(approvedQuery.data || []),
  ];

  return {
    data: entries,
    looseEntries: looseQuery.data || [],
    approvedEntries: approvedQuery.data || [],
    isLoading: looseQuery.isLoading || approvedQuery.isLoading,
    isFetching: looseQuery.isFetching || approvedQuery.isFetching,
    isError: looseQuery.isError || approvedQuery.isError,
    error: looseQuery.error || approvedQuery.error,
    refetch: () => {
      looseQuery.refetch();
      approvedQuery.refetch();
    },
  };
};

/**
 * Fetch a single entry for editing
 */
export const useEntryQuery = (id: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.inventory.detail(id!),
    queryFn: () => fetchEntryForEdit(id!),
    enabled: !!id,
  });
};

/**
 * Fetch unattached flow meters for a given datetime
 */
export const useUnattachedFlowMetersQuery = (
  dateTime: string | undefined,
  bookkeepingEntryId: string | undefined
) => {
  return useQuery({
    queryKey: queryKeys.flowMeters.unattached(dateTime),
    queryFn: () => fetchUnattachedFlowMeters(dateTime!, bookkeepingEntryId || ''),
    enabled: !!dateTime,
  });
};

/**
 * Fetch unattached hydrogen peroxide flow meters for a given datetime
 */
export const useUnattachedFlowMetersHydrogenPeroxideQuery = (
  dateTime: string | undefined,
  bookkeepingEntryId: string | undefined
) => {
  return useQuery({
    queryKey: [...queryKeys.flowMeters.unattached(dateTime), 'hydrogenPeroxide'] as const,
    queryFn: () => fetchUnattachedFlowMetersHydrogenPeroxide(dateTime!, bookkeepingEntryId || ''),
    enabled: !!dateTime,
  });
};

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Create a new bookkeeping entry
 */
export const useCreateEntryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createEntry,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tanks.balances() });
      toastService.success({ title: 'save_succeeded' });
    },
    onError: (error: unknown) => {
      showApiError(error, 'save_failed');
    },
  });
};

/**
 * Create a product mixture entry (creates multiple entries)
 */
export const useCreateProductMixtureEntryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createProductMixtureEntry,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tanks.balances() });
      toastService.success({ title: 'save_succeeded' });
    },
    onError: (error: unknown) => {
      showApiError(error, 'save_failed');
    },
  });
};

/**
 * Update an existing bookkeeping entry
 */
export const useUpdateEntryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateEntry,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tanks.balances() });
      // Update the specific entry in cache
      queryClient.setQueryData(queryKeys.inventory.detail(data.id), data);
      toastService.success({ title: 'save_succeeded' });
    },
    onError: (error: unknown) => {
      showApiError(error, 'save_failed');
    },
  });
};

/**
 * Update notes on an approved entry
 */
export const useUpdateApprovedEntryNotesMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateApprovedEntryNotes,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
      queryClient.setQueryData(queryKeys.inventory.detail(data.id), data);
      toastService.success({ title: 'save_succeeded' });
    },
    onError: (error: unknown) => {
      showApiError(error, 'save_failed');
    },
  });
};

/**
 * Delete a bookkeeping entry
 */
export const useDeleteEntryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteEntry,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tanks.balances() });
      toastService.success({ title: 'entry.entry_deleted' });
    },
    onError: (error: unknown) => {
      showApiError(error, 'save_failed');
    },
  });
};

// ============================================================================
// Waybill Generation
// ============================================================================

/**
 * Download waybill document as Excel file
 */
export const downloadWaybillDocument = async (bookkeepingId: string): Promise<void> => {
  const response = await fetch(`/api/bookkeepings/waybill/${bookkeepingId}`, {
    method: 'GET',
    headers: {
      'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to download waybill');
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `waybill-${bookkeepingId}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const useDownloadWaybillMutation = () => {
  return useMutation({
    mutationFn: downloadWaybillDocument,
    onError: (error: unknown) => {
      showApiError(error, 'entry.waybill_download_failed');
    },
  });
};
